import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, TextField, IconButton, Button, Paper, CircularProgress,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import ReplyIcon from '@mui/icons-material/Reply';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import { db } from '../../../../firebase/config';
import {
  collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc,
  query, orderBy, getDocs, where, serverTimestamp,
} from 'firebase/firestore';
import { useAuth } from '../../../../contexts/AuthContext';
import { MAPPN_ACCENT } from '../constants';
import { toast } from 'sonner';
import { authFetch } from '../../../../utils/authFetch';

const INPUT_SX = {
  '& .MuiOutlinedInput-root': {
    color: 'white', fontSize: '0.83rem',
    bgcolor: 'rgba(255,255,255,0.03)',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
    '&.Mui-focused fieldset': { borderColor: MAPPN_ACCENT },
  },
  '& textarea::placeholder, & input::placeholder': { color: 'rgba(255,255,255,0.25)' },
};

function relTime(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const diff = Date.now() - d.getTime();
  if (diff < 60000) return 'Nu';
  if (diff < 3600000) return `${Math.floor(diff / 60000)} min`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)} h`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)} d`;
  return d.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' });
}

const AV_COLORS = ['#1565C0','#2E7D32','#E65100','#6A1B9A','#00838F','#C62828','#F57C00'];
function avatarColor(name) {
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h = (h * 31 + name.charCodeAt(i)) % AV_COLORS.length;
  return AV_COLORS[h];
}

function Av({ name, size = 28 }) {
  return (
    <Box sx={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      bgcolor: avatarColor(name || '?'),
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Typography sx={{ color: 'white', fontWeight: 700, fontSize: size * 0.43, lineHeight: 1 }}>
        {(name || '?')[0].toUpperCase()}
      </Typography>
    </Box>
  );
}

function renderText(text) {
  return text.split(/(@\w+)/g).map((part, i) =>
    part.startsWith('@')
      ? <Box key={i} component="span" sx={{ color: MAPPN_ACCENT, fontWeight: 600 }}>{part}</Box>
      : part
  );
}

function CommentItem({
  comment, replies, currentUserId,
  editingId, editText, onEditChange, onEditSave, onEditStart, onEditCancel,
  onReply, onResolve, onDelete,
}) {
  const isOwn = comment.authorId === currentUserId;

  return (
    <Box sx={{ opacity: comment.resolved ? 0.5 : 1, transition: 'opacity 0.2s' }}>
      <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
        <Av name={comment.authorName} size={28} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 0.3, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: 'white' }}>
              {comment.authorName}
            </Typography>
            <Typography sx={{ fontSize: '0.67rem', color: 'rgba(255,255,255,0.3)' }}>
              {relTime(comment.createdAt)}{comment.edited ? ' · redigerad' : ''}
            </Typography>
            {comment.resolved && (
              <Typography sx={{ fontSize: '0.65rem', color: '#66BB6A', fontWeight: 700 }}>✓ Löst</Typography>
            )}
          </Box>

          {editingId === comment.id ? (
            <Box>
              <TextField
                value={editText}
                onChange={e => onEditChange(e.target.value)}
                multiline fullWidth size="small" autoFocus sx={INPUT_SX}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onEditSave(comment); }
                  if (e.key === 'Escape') onEditCancel();
                }}
              />
              <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                <Button size="small" onClick={() => onEditSave(comment)}
                  sx={{ color: MAPPN_ACCENT, textTransform: 'none', fontSize: '0.72rem', p: 0, minWidth: 0 }}>
                  Spara
                </Button>
                <Button size="small" onClick={onEditCancel}
                  sx={{ color: 'rgba(255,255,255,0.3)', textTransform: 'none', fontSize: '0.72rem', p: 0, minWidth: 0 }}>
                  Avbryt
                </Button>
              </Box>
            </Box>
          ) : (
            <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.8)', lineHeight: 1.6, wordBreak: 'break-word' }}>
              {renderText(comment.text)}
            </Typography>
          )}

          {editingId !== comment.id && (
            <Box sx={{ display: 'flex', gap: 0.25, mt: 0.4 }}>
              <IconButton size="small" onClick={() => onReply(comment)}
                sx={{ color: 'rgba(255,255,255,0.2)', '&:hover': { color: MAPPN_ACCENT }, p: 0.4 }}>
                <ReplyIcon sx={{ fontSize: '0.85rem' }} />
              </IconButton>
              <IconButton size="small" onClick={() => onResolve(comment)}
                sx={{ color: comment.resolved ? '#66BB6A' : 'rgba(255,255,255,0.2)', '&:hover': { color: '#66BB6A' }, p: 0.4 }}>
                {comment.resolved
                  ? <ReplayIcon sx={{ fontSize: '0.85rem' }} />
                  : <CheckCircleOutlineIcon sx={{ fontSize: '0.85rem' }} />}
              </IconButton>
              {isOwn && (
                <>
                  <IconButton size="small" onClick={() => onEditStart(comment)}
                    sx={{ color: 'rgba(255,255,255,0.2)', '&:hover': { color: 'rgba(255,255,255,0.6)' }, p: 0.4 }}>
                    <EditOutlinedIcon sx={{ fontSize: '0.85rem' }} />
                  </IconButton>
                  <IconButton size="small" onClick={() => onDelete(comment)}
                    sx={{ color: 'rgba(255,255,255,0.2)', '&:hover': { color: '#EF5350' }, p: 0.4 }}>
                    <DeleteOutlineIcon sx={{ fontSize: '0.85rem' }} />
                  </IconButton>
                </>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Replies */}
      {replies.length > 0 && (
        <Box sx={{ ml: 4.75, mt: 1, pl: 1.5, borderLeft: '2px solid rgba(255,255,255,0.06)' }}>
          {replies.map(r => (
            <Box key={r.id} sx={{ mb: 1.25 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Av name={r.authorName} size={22} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 0.3 }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'white' }}>{r.authorName}</Typography>
                    <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.3)' }}>
                      {relTime(r.createdAt)}{r.edited ? ' · redigerad' : ''}
                    </Typography>
                  </Box>

                  {editingId === r.id ? (
                    <Box>
                      <TextField
                        value={editText}
                        onChange={e => onEditChange(e.target.value)}
                        multiline fullWidth size="small" autoFocus sx={INPUT_SX}
                        onKeyDown={e => {
                          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onEditSave(r); }
                          if (e.key === 'Escape') onEditCancel();
                        }}
                      />
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                        <Button size="small" onClick={() => onEditSave(r)}
                          sx={{ color: MAPPN_ACCENT, textTransform: 'none', fontSize: '0.72rem', p: 0, minWidth: 0 }}>
                          Spara
                        </Button>
                        <Button size="small" onClick={onEditCancel}
                          sx={{ color: 'rgba(255,255,255,0.3)', textTransform: 'none', fontSize: '0.72rem', p: 0, minWidth: 0 }}>
                          Avbryt
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.75)', lineHeight: 1.5, wordBreak: 'break-word' }}>
                      {renderText(r.text)}
                    </Typography>
                  )}

                  {editingId !== r.id && r.authorId === currentUserId && (
                    <Box sx={{ display: 'flex', gap: 0.25, mt: 0.25 }}>
                      <IconButton size="small" onClick={() => onEditStart(r)}
                        sx={{ color: 'rgba(255,255,255,0.2)', '&:hover': { color: 'rgba(255,255,255,0.6)' }, p: 0.3 }}>
                        <EditOutlinedIcon sx={{ fontSize: '0.8rem' }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => onDelete(r)}
                        sx={{ color: 'rgba(255,255,255,0.2)', '&:hover': { color: '#EF5350' }, p: 0.3 }}>
                        <DeleteOutlineIcon sx={{ fontSize: '0.8rem' }} />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

export default function CommentThread({ caseId, componentId }) {
  const { currentUser, userProfile } = useAuth();
  const [comments, setComments]     = useState([]);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [text, setText]             = useState('');
  const [replyTo, setReplyTo]       = useState(null);
  const [mentionSearch, setMentionSearch] = useState(null);
  const [editingId, setEditingId]   = useState(null);
  const [editText, setEditText]     = useState('');
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!caseId || !componentId || componentId === '__new__') return;
    const q = query(
      collection(db, 'mappnCases', caseId, 'components', componentId, 'comments'),
      orderBy('createdAt')
    );
    return onSnapshot(q, snap =>
      setComments(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
  }, [caseId, componentId]);

  useEffect(() => {
    if (!userProfile?.companyId) return;
    getDocs(query(collection(db, 'users'), where('companyId', '==', userProfile.companyId)))
      .then(snap => setCompanyUsers(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [userProfile?.companyId]);

  async function refreshCounters() {
    try {
      const snap = await getDocs(
        collection(db, 'mappnCases', caseId, 'components', componentId, 'comments')
      );
      const all = snap.docs.map(d => d.data());
      const topLevel = all.filter(c => !c.parentCommentId);
      await updateDoc(doc(db, 'mappnCases', caseId, 'components', componentId), {
        commentCount: topLevel.length,
        unresolvedCommentCount: topLevel.filter(c => !c.resolved).length,
      });
    } catch (e) { /* non-critical */ }
  }

  function extractMentions(txt) {
    const matches = txt.match(/@(\w+)/g) || [];
    return matches.flatMap(m => {
      const slug = m.slice(1).toLowerCase();
      const user = companyUsers.find(u => (u.name || '').toLowerCase().replace(/\s+/g, '') === slug);
      return user ? [user.id] : [];
    });
  }

  function handleTextChange(e) {
    const val = e.target.value;
    setText(val);
    const pos = e.target.selectionStart;
    const m = /@(\w*)$/.exec(val.slice(0, pos));
    setMentionSearch(m ? m[1].toLowerCase() : null);
  }

  function insertMention(user) {
    const pos = inputRef.current?.selectionStart ?? text.length;
    const before = text.slice(0, pos);
    const after = text.slice(pos);
    const m = /@(\w*)$/.exec(before);
    if (m) {
      const slug = (user.name || '').replace(/\s+/g, '');
      setText(before.slice(0, m.index) + `@${slug} ` + after);
    }
    setMentionSearch(null);
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  async function submit() {
    if (!text.trim() || !currentUser || submitting) return;
    setSubmitting(true);
    try {
      const mentions = extractMentions(text);
      await addDoc(
        collection(db, 'mappnCases', caseId, 'components', componentId, 'comments'),
        {
          authorId: currentUser.uid,
          authorName: userProfile?.name || currentUser.displayName || 'Okänd',
          authorPhotoUrl: userProfile?.photoURL || currentUser.photoURL || '',
          text: text.trim(),
          parentCommentId: replyTo?.id || null,
          mentions,
          resolved: false,
          edited: false,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }
      );
      await refreshCounters();
      if (mentions.length) notifyMentioned(mentions, text);
      setText('');
      setReplyTo(null);
    } catch (e) {
      toast.error('Kunde inte posta kommentar');
    }
    setSubmitting(false);
  }

  async function notifyMentioned(mentionIds, commentText) {
    const mentioned = companyUsers.filter(u => mentionIds.includes(u.id) && u.email);
    if (!mentioned.length) return;
    try {
      await authFetch('/api/mappn-comment-notify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mentions: mentioned.map(u => ({ name: u.name, email: u.email })),
          authorName: userProfile?.name || currentUser?.displayName || 'Okänd',
          commentText,
        }),
      });
    } catch (e) { /* best-effort */ }
  }

  async function resolveComment(comment) {
    try {
      await updateDoc(
        doc(db, 'mappnCases', caseId, 'components', componentId, 'comments', comment.id),
        { resolved: !comment.resolved, updatedAt: serverTimestamp() }
      );
      await refreshCounters();
    } catch (e) {
      toast.error('Kunde inte uppdatera');
    }
  }

  async function saveEdit(comment) {
    if (!editText.trim()) return;
    try {
      await updateDoc(
        doc(db, 'mappnCases', caseId, 'components', componentId, 'comments', comment.id),
        { text: editText.trim(), edited: true, updatedAt: serverTimestamp() }
      );
      setEditingId(null);
      setEditText('');
    } catch (e) {
      toast.error('Kunde inte spara');
    }
  }

  async function handleDelete(comment) {
    if (!window.confirm('Ta bort kommentaren?')) return;
    try {
      await deleteDoc(
        doc(db, 'mappnCases', caseId, 'components', componentId, 'comments', comment.id)
      );
      await refreshCounters();
    } catch (e) {
      toast.error('Kunde inte ta bort');
    }
  }

  const topLevel = comments.filter(c => !c.parentCommentId);
  const openCount = topLevel.filter(c => !c.resolved).length;
  const mentionOptions = mentionSearch !== null
    ? companyUsers.filter(u => (u.name || '').toLowerCase().includes(mentionSearch)).slice(0, 6)
    : [];

  return (
    <Box sx={{ bgcolor: 'rgba(255,255,255,0.025)', borderRadius: 1.5, border: '1px solid rgba(255,255,255,0.05)', p: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="caption" fontWeight={700} sx={{
          textTransform: 'uppercase', letterSpacing: 1,
          color: 'rgba(255,255,255,0.35)',
        }}>
          Kommentarer
        </Typography>
        {topLevel.length > 0 && (
          <Box sx={{
            ml: 1, fontSize: '0.62rem', fontWeight: 700,
            bgcolor: openCount > 0 ? MAPPN_ACCENT + '22' : '#66BB6A22',
            color: openCount > 0 ? MAPPN_ACCENT : '#66BB6A',
            px: 0.8, py: 0.1, borderRadius: 0.75,
          }}>
            {openCount > 0 ? `${openCount} öppna` : 'Alla lösta'}
          </Box>
        )}
      </Box>

      {/* Comment list */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: topLevel.length ? 2 : 1.5 }}>
        {topLevel.map(comment => (
          <CommentItem
            key={comment.id}
            comment={comment}
            replies={comments.filter(c => c.parentCommentId === comment.id)}
            currentUserId={currentUser?.uid}
            editingId={editingId}
            editText={editText}
            onEditChange={setEditText}
            onEditSave={saveEdit}
            onEditStart={c => { setEditingId(c.id); setEditText(c.text); }}
            onEditCancel={() => { setEditingId(null); setEditText(''); }}
            onReply={c => { setReplyTo(c); setTimeout(() => inputRef.current?.focus(), 0); }}
            onResolve={resolveComment}
            onDelete={handleDelete}
          />
        ))}
        {topLevel.length === 0 && (
          <Typography sx={{ fontSize: '0.76rem', color: 'rgba(255,255,255,0.2)', textAlign: 'center', py: 0.5 }}>
            Inga kommentarer ännu
          </Typography>
        )}
      </Box>

      {/* Reply indicator */}
      {replyTo && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, mb: 0.75,
          px: 1, py: 0.5, bgcolor: MAPPN_ACCENT + '11', borderRadius: 1,
          border: `1px solid ${MAPPN_ACCENT}22`,
        }}>
          <ReplyIcon sx={{ fontSize: '0.8rem', color: MAPPN_ACCENT }} />
          <Typography sx={{ fontSize: '0.74rem', color: MAPPN_ACCENT, flex: 1 }}>
            Svarar {replyTo.authorName}
          </Typography>
          <IconButton size="small" onClick={() => setReplyTo(null)} sx={{ color: 'rgba(255,255,255,0.3)', p: 0.25 }}>
            <CloseIcon sx={{ fontSize: '0.75rem' }} />
          </IconButton>
        </Box>
      )}

      {/* @-mention dropdown */}
      {mentionSearch !== null && mentionOptions.length > 0 && (
        <Paper sx={{ bgcolor: '#1e2530', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 1, mb: 0.5, overflow: 'hidden' }}>
          {mentionOptions.map(u => (
            <Box
              key={u.id}
              onClick={() => insertMention(u)}
              sx={{ px: 1.5, py: 0.75, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1, '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}
            >
              <Av name={u.name} size={22} />
              <Typography sx={{ fontSize: '0.8rem', color: 'white' }}>{u.name}</Typography>
            </Box>
          ))}
        </Paper>
      )}

      {/* Input row */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
        <Av name={userProfile?.name || currentUser?.displayName || '?'} size={26} />
        <TextField
          inputRef={inputRef}
          value={text}
          onChange={handleTextChange}
          placeholder={replyTo ? `Svara ${replyTo.authorName}…` : 'Skriv en kommentar… (@ för att nämna)'}
          multiline maxRows={4} fullWidth size="small" sx={INPUT_SX}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey && mentionSearch === null) {
              e.preventDefault();
              submit();
            }
          }}
        />
        <IconButton
          onClick={submit}
          disabled={!text.trim() || submitting}
          sx={{ color: text.trim() ? MAPPN_ACCENT : 'rgba(255,255,255,0.15)', p: 0.75, flexShrink: 0 }}
        >
          {submitting
            ? <CircularProgress size={18} sx={{ color: MAPPN_ACCENT }} />
            : <SendIcon sx={{ fontSize: '1.1rem' }} />}
        </IconButton>
      </Box>
    </Box>
  );
}
